import React, { useState, useEffect, useRef } from 'react';
import { Search, FileText, X, ChevronDown, ExternalLink, Loader2 } from 'lucide-react';
import { communicationService } from '../../../services/communicationService';

const TemplateSelector = ({ selectedTemplate, onSelectTemplate, onNavigateToTemplates }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [filterMessageType, setFilterMessageType] = useState('all');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const loadTemplates = async () => {
      setLoading(true);
      try {
      const filters = {
        isActive: true,
        page: 1,
        pageSize: 50, // Load first 50 active templates
        triggerType: 'MANUAL' // Only show templates that can be used manually (MANUAL or BOTH)
      };

      if (filterMessageType !== 'all') {
        filters.messageType = filterMessageType;
      }

      if (searchQuery.trim()) {
        filters.search = searchQuery.trim();
      }

        const result = await communicationService.getAllTemplates(filters);
        setTemplates(result.data || []);
      } catch (error) {
        console.error('Error loading templates:', error);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      // Debounce search queries
      if (searchQuery.trim()) {
        const timer = setTimeout(() => {
          loadTemplates();
        }, 300);
        return () => clearTimeout(timer);
      } else {
        // Load immediately when dropdown opens or filter changes
        loadTemplates();
      }
    }
  }, [isOpen, filterMessageType, searchQuery]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (template) => {
    onSelectTemplate(template);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onSelectTemplate(null);
  };

  const filteredTemplates = templates.filter(template => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      template.name?.toLowerCase().includes(query) ||
      template.template_message?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium text-gray-700">
            Select Template
          </label>
          <button
            onClick={onNavigateToTemplates}
            className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
          >
            <ExternalLink className="w-3 h-3" />
            Manage Templates
          </button>
        </div>

        {/* Selected Template Display */}
        {selectedTemplate ? (
          <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <FileText className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <span className="font-medium text-sm text-gray-900 truncate">
                    {selectedTemplate.name}
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">
                    {selectedTemplate.message_type}
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded bg-purple-100 text-purple-700">
                    {selectedTemplate.template_type}
                  </span>
                  {selectedTemplate.is_runtime_message && (
                    <span className="text-xs px-2 py-0.5 rounded bg-indigo-100 text-indigo-700">
                      Runtime
                    </span>
                  )}
                  {selectedTemplate.is_static_message && !selectedTemplate.is_runtime_message && (
                    <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700">
                      Static
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={handleClear}
                className="p-1 hover:bg-blue-100 rounded transition-colors flex-shrink-0"
                title="Clear selection"
              >
                <X className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          </div>
        ) : null}

        {/* Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-white text-left text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 transition-colors"
          >
            <span className="text-gray-500">
              {selectedTemplate ? selectedTemplate.name : 'Select a template...'}
            </span>
            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>

          {isOpen && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-hidden">
              {/* Search and Filter */}
              <div className="p-3 border-b border-gray-200 space-y-2">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search templates..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={filterMessageType}
                    onChange={(e) => {
                      setFilterMessageType(e.target.value);
                      setSearchQuery('');
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">All Channels</option>
                    <option value="SMS">SMS</option>
                    <option value="WHATSAPP">WhatsApp</option>
                  </select>
                </div>
              </div>

              {/* Template List */}
              <div className="overflow-y-auto max-h-64">
                {loading ? (
                  <div className="p-4 text-center">
                    <Loader2 className="w-5 h-5 animate-spin text-blue-600 mx-auto mb-2" />
                    <p className="text-xs text-gray-500">Loading templates...</p>
                  </div>
                ) : filteredTemplates.length === 0 ? (
                  <div className="p-4 text-center">
                    <FileText className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-xs text-gray-500">
                      {searchQuery.trim() ? 'No templates found' : 'No active templates'}
                    </p>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsOpen(false);
                        onNavigateToTemplates();
                      }}
                      className="mt-2 text-xs text-blue-600 hover:text-blue-700"
                    >
                      Create template
                    </button>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {filteredTemplates.map(template => (
                      <button
                        key={template.id}
                        onClick={() => handleSelect(template)}
                        className={`w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors ${
                          selectedTemplate?.id === template.id ? 'bg-blue-50' : ''
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm text-gray-900 truncate">
                              {template.name}
                            </div>
                            {template.template_message && (
                              <div className="text-xs text-gray-500 mt-0.5 line-clamp-1">
                                {template.template_message}
                              </div>
                            )}
                            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                              <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                                {template.message_type}
                              </span>
                              <span className="text-xs px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">
                                {template.template_type}
                              </span>
                              {template.is_runtime_message && (
                                <span className="text-xs px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700">
                                  Runtime
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplateSelector;

